import { action } from '@ember/object';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import type EmergencyNotificationConfigurationModel from 'garaje/models/emergency-notification-configuration';
import type EmployeeModel from 'garaje/models/employee';
import type AjaxService from 'garaje/services/ajax';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';

interface LocalEmergencyResponseTeamArgs {
  emergencyNotificationConfiguration: EmergencyNotificationConfigurationModel;
}

interface EligibleEmployeeData {
  id: string;
  type: string;
  attributes: {
    name: string;
    [key: string]: unknown;
  };
}

interface EligibleEmployeesResponse {
  data: EligibleEmployeeData[];
}

export default class LocalEmergencyResponseTeam extends Component<LocalEmergencyResponseTeamArgs> {
  @service declare ajax: AjaxService;
  @service declare flashMessages: FlashMessagesService;
  @service declare store: Store;
  @service declare featureFlags: FeatureFlagsService;

  @tracked selectedAdmins: EmployeeModel[] = [];
  @tracked editingAdmins: EmployeeModel[] = [];
  @tracked isLoading = true;
  @tracked isPanelOpen = false;

  constructor(owner: unknown, args: LocalEmergencyResponseTeamArgs) {
    super(owner, args);
    if (this.isFeatureEnabled) {
      void this.loadSelectedAdmins.perform();
    }
  }

  get isFeatureEnabled(): boolean {
    return this.featureFlags.isEnabled('emno-chat-web');
  }

  get locationId(): string | undefined {
    return this.args.emergencyNotificationConfiguration.belongsTo('location').id();
  }

  searchEligibleEmployees = task({ restartable: true }, async (query: string): Promise<EmployeeModel[]> => {
    if (!this.locationId) {
      return [];
    }

    try {
      const url = `/a/visitors/api/v3/locations/${this.locationId}/emergency-notification-eligible-employees`;
      const response = await this.ajax.request<EligibleEmployeesResponse>(url, {
        data: {
          'filter[name]': query,
        },
      });

      // Map the response data to employee models
      const employees = response.data.map((employeeData) => {
        return this.store.push({
          data: {
            id: employeeData.id,
            type: 'employee',
            attributes: employeeData.attributes,
          },
        }) as EmployeeModel;
      });

      return employees;
    } catch (e) {
      this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e));
      return [];
    }
  });

  loadSelectedAdmins = task({ drop: true }, async () => {
    try {
      this.isLoading = true;

      const employeeIds = this.args.emergencyNotificationConfiguration.emergencyResponseTeamEmployeeIds;

      if (employeeIds.length > 0) {
        const localEmergencyResponseTeam = await this.store.query('employee', {
          filter: {
            id: employeeIds.join(','),
            deleted: false,
          },
        });
        this.selectedAdmins = localEmergencyResponseTeam.slice();
      }
    } catch (e) {
      this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e));
    } finally {
      this.isLoading = false;
    }
  });

  saveSelectedAdmins = task({ drop: true }, async (admins: EmployeeModel[]) => {
    try {
      const { emergencyNotificationConfiguration } = this.args;

      emergencyNotificationConfiguration.emergencyResponseTeamEmployeeIds = admins.map((employee) => +employee.id);
      await emergencyNotificationConfiguration.save();

      this.selectedAdmins = admins;
      this.isPanelOpen = false;
      this.editingAdmins = [];
      this.flashMessages.showAndHideFlash('success', 'Saved!');
    } catch (e) {
      this.args.emergencyNotificationConfiguration.rollbackAttributes();
      this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e));
    }
  });

  @action
  handleAdminsChange(admins: EmployeeModel[]): void {
    this.editingAdmins = admins;
  }

  @action
  startEditing(): void {
    this.isPanelOpen = true;
    this.editingAdmins = this.selectedAdmins.slice();
  }

  @action
  cancelEditing(): void {
    this.isPanelOpen = false;
    this.editingAdmins = [];
  }

  @action
  saveChanges(): void {
    void this.saveSelectedAdmins.perform(this.editingAdmins);
  }
}
